import React, {
  useEffect,
  useReducer,
  useState,
  useCallback,
  useTransition,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { get, update } from "@/actions/sliders.action";
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  TextField,
} from "@mui/material";
import { url } from "@/utils";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAll } from "@/actions/image.actions";
import AddItemModal from "./AddItemModel";
import { logger } from "@/lib/default-logger";
import { useTranslation } from "react-i18next";

const initialState = {
  title: "",
  description: "",
  interval: 8000,
  transparency: 30,
  items: [],
  item: {
    title: "",
    description: "",
    image: "",
    order: 0,
  },
};

type State = {
  title: string;
  description: string;
  interval: number;
  transparency: number;
  items: { title: string; description: string; image: string; order: number }[];
  item: { title: string; description: string; image: string; order: number };
};

type Action =
  | { type: "SET_FIELD"; field: string; value: any }
  | { type: "SET_ITEM_FIELD"; field: string; value: any }
  | { type: "ADD_ITEM" }
  | { type: "REMOVE_ITEM"; index: number }
  | { type: "SET_SLIDER"; payload: any };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "SET_ITEM_FIELD":
      return {
        ...state,
        item: { ...state.item, [action.field]: action.value },
      };
    case "ADD_ITEM":
      return {
        ...state,
        items: [...state.items, state.item],
        item: initialState.item,
      };
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((_, i) => i !== action.index),
      };
    case "SET_SLIDER":
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

export default function UpdateSlider() {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, localDispatch] = useReducer(reducer, initialState);
  const [isModalOpen, setModalOpen] = useState(false);

  const { status, currentSlider } = useSelector((state: any) => state.sliders);
  const { images, status: imageStatus } = useSelector(
    (state: any) => state.images
  );

  useEffect(() => {
    if (id) {
      dispatch(get(id) as any);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (imageStatus === "idle") {
      dispatch(getAll() as any);
    }
  }, [imageStatus, dispatch]);

  useEffect(() => {
    if (currentSlider) {
      localDispatch({
        type: "SET_SLIDER",
        payload: {
          title: currentSlider.title || "",
          description: currentSlider.description || "",
          interval: currentSlider.interval || 8000,
          transparency: currentSlider.transparency || 30,
          items: currentSlider.items || [],
        },
      });
    }
  }, [currentSlider]);

  // ✅ Fixing state handling for main slider fields
  const handleChange = useCallback(
    (field: string, value: any) => {
      localDispatch({ type: "SET_FIELD", field, value });
    },
    [localDispatch]
  );

  const handleItemChange = useCallback(
    (field: string, value: any) => {
      localDispatch({ type: "SET_ITEM_FIELD", field, value });
    },
    [localDispatch]
  );

  const addItem = () => {
    if (!state.item.title || !state.item.image) return;
    localDispatch({ type: "ADD_ITEM" });
    setModalOpen(false);
  };

  const handleSubmit = () => {
    const { item, ...slider } = state;
    logger.log(slider);
    dispatch(update(id!, slider) as any);
    if (status === "success") navigate(url("Admin.Sliders"));
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6">Update Slider</Typography>

        {/* ✅ Main Slider Fields Restored */}
        <TextField
          label={t("Title")}
          fullWidth
          margin="normal"
          value={state.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />

        <TextField
          label={t("Description")}
          fullWidth
          margin="normal"
          value={state.description}
          onChange={(e) => handleChange("description", e.target.value)}
        />

        <TextField
          label={t("Interval")}
          fullWidth
          margin="normal"
          type="number"
          value={state.interval}
          onChange={(e) => handleChange("interval", Number(e.target.value))}
        />

        <TextField
          label={t("Transparency")}
          fullWidth
          margin="normal"
          type="number"
          value={state.transparency}
          onChange={(e) => handleChange("transparency", Number(e.target.value))}
        />

        {/* ✅ Existing Items */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {state.items.map((item, index) => {
            const image: any = item.image;
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    {image && (
                      <CardMedia
                        component="img"
                        height="100"
                        width="100"
                        image={image.url}
                        alt={image.altText}
                        sx={{
                          objectFit: "cover",
                          height: "10rem",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                    <Typography>
                      {t("Title")} : {item.title}
                    </Typography>
                    <Typography>
                      {t("description")} : {item.description}
                    </Typography>
                    <Typography>
                      {t("order")} : {item.order}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      color="error"
                      onClick={() =>
                        localDispatch({ type: "REMOVE_ITEM", index })
                      }
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Open Modal Button */}
        <Button onClick={() => setModalOpen(true)} sx={{ mt: 2 }}>
          {t("add_item")}
        </Button>

        {/* Modal Component */}
        <AddItemModal
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
          item={state.item}
          handleItemChange={handleItemChange}
          images={images}
          addItem={addItem}
        />

        {/* Save Button */}
        <Button
          onClick={handleSubmit}
          sx={{ mt: 2 }}
          variant="contained"
          color="primary"
          fullWidth
        >
          {t("save_changes")}
        </Button>
      </CardContent>
    </Card>
  );
}
